// modules/elearning/students/StudentFilter.jsx
import React, { useEffect, useState } from "react";
import { 
  Search, 
  RotateCw, 
  SlidersHorizontal, 
  Plus, 
  MoreVertical, 
  ChevronDown,
  FileSpreadsheet,
  UploadCloud,
  DownloadCloud,
  Eraser,
  BookOpen,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getSubjectsLookup } from "../../../api/elearning/subjects/subjectInfo";
import { getLevelOptions } from "../../../utils/dropdownOptions";
import { showErrorToast } from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";

const StudentFilter = ({
  onNewClick,
  onImportClick,
  onFilterChange,
  onExportClick,
  exporting = false,
}) => {
  const [advanced, setAdvanced] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [filters, setFilters] = useState({
    keyword: "",
    category_id: "",
    classify: "",
    is_active: "",
    level: "",
    no_class: "",
  });

  const levelOptions = getLevelOptions();
  const locationOptions = [
    { value: "truong-hoc", label: "Trường học" },
    { value: "trung-tam", label: "Trung tâm" },
  ];
  const statusOptions = [
    { value: "true", label: "Hoạt động" },
    { value: "false", label: "Tạm ngưng" },
  ];
  const noClassOptions = [
    { value: "true", label: "Chưa có lớp học" },
  ];
  const isFiltering = Object.values(filters).some((v) => v !== "");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const categories = await getSubjectsLookup();
        setSubjectOptions(categories.map(cat => ({ value: cat.id, label: cat.name })));
      } catch (err) { console.error(err); }
    };
    fetchSubjects();
  }, []);

  // Debounce tìm kiếm
  useEffect(() => {
    const timer = setTimeout(() => onFilterChange?.(filters), 400);
    return () => clearTimeout(timer);
  }, [filters.keyword]);

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const cleared = {
      keyword: "",
      category_id: "",
      classify: "",
      is_active: "",
      level: "",
      no_class: "",
    };
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const openGuide = () => {
    const guideUrl = `${window.location.origin}/guides/elearning-students-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  };

  // ================= RENDER COMPONENTS =================
  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 
        ${active 
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm" 
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
        }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, options, onChange }) => {
    // Trên Desktop dùng giao diện sạch sẽ với icon ChevronDown
    return (
      <div className="flex flex-col gap-1.5 w-full">
        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
          {label}
        </label>
        <div className="relative group">
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-10 rounded-2xl text-sm
                       focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
          >
            <option value="">Tất cả {label}</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {/* Thay thế icon mặc định bằng icon Lucide để đồng bộ */}
          <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors" />
        </div>
      </div>
    );
  };

  if (isMobile) {
    return (
      <div className="mb-6 space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm tên, mã học sinh, lớp, đơn vị..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all"
            />
          </div>
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} />
        </div>

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc nâng cao"
          footer={
            <button
              type="button"
              onClick={() => setAdvanced(false)}
              className="w-full py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200"
            >
              ÁP DỤNG
            </button>
          }
        >
          <div className="grid grid-cols-1 gap-4">
            <SelectBox label="Môn học" value={filters.category_id} options={subjectOptions} onChange={(v) => updateFilter("category_id", v)} />
            <SelectBox label="Trình độ" value={filters.level} options={levelOptions} onChange={(v) => updateFilter("level", v)} />
            <SelectBox label="Địa điểm" value={filters.classify} options={locationOptions} onChange={(v) => updateFilter("classify", v)} />
            <SelectBox label="Trạng thái" value={filters.is_active} options={statusOptions} onChange={(v) => updateFilter("is_active", v)} />
            <SelectBox label="Phân lớp" value={filters.no_class} options={noClassOptions} onChange={(v) => updateFilter("no_class", v)} />
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm mb-6">
      <div className="flex items-center justify-between gap-4">
        {/* Nhóm tìm kiếm & nút chức năng */}
        <div className="flex items-center gap-3 flex-1">
          <div className="relative w-full max-w-sm group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm tên, mã học sinh, lớp, đơn vị..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton icon={RotateCw} onClick={() => onFilterChange?.({ ...filters })} title="Làm mới" />
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} title="Lọc nâng cao" />
          
          {isFiltering && (
             <button onClick={clearFilters} className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2">
                <Eraser size={14} /> Xóa lọc
             </button>
          )}
        </div>


        {/* Nhóm hành động chính */}
        <div className="flex items-center gap-3">      
          {/* Nhập Excel */}
        {hasPermission("elearning.classes", "C") && onImportClick && (
            <button
              type="button"
              onClick={() => onImportClick()}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-red-200 text-red-700 rounded-full font-bold hover:bg-red-50 shadow-sm transition-all active:scale-95 uppercase text-xs"
            >
              <FileSpreadsheet size={18} />
              Nhập Excel
            </button>
          )}

          {/* Nút Thêm mới */}
          {hasPermission("elearning.students", "C") && (
            <button
              onClick={onNewClick}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full font-bold hover:shadow-lg hover:shadow-red-100 transition-all active:scale-95 uppercase text-xs tracking-wider"
            >
              <Plus size={18} />
              Thêm học viên
            </button>
          )}
          
          <div className="relative">
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all ${moreOpen ? 'bg-gray-100 border-gray-300' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
            >
              <MoreVertical size={18} />
            </button>
            
            <AnimatePresence>
              {moreOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  className="absolute right-0 mt-3 w-56 bg-white border border-gray-100 rounded-[1.5rem] shadow-2xl z-50 p-2 overflow-hidden"
                  onMouseLeave={() => setMoreOpen(false)}
                >
                  {[
                    { icon: FileSpreadsheet, label: "Tải Template CSV", action: () => {} },
                    { icon: UploadCloud, label: "Import dữ liệu", action: onImportClick }, // ✅ Gắn prop mới
                    {
                      icon: DownloadCloud,
                      label: exporting ? "Đang xuất…" : "Export dữ liệu",
                      action: onExportClick,
                      disabled: exporting,
                    },
                    { icon: BookOpen, label: "Hướng dẫn sử dụng", action: openGuide },
                  ].map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      disabled={item.disabled}
                      className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all disabled:opacity-50 disabled:pointer-events-none"
                      onClick={item.action || (() => showErrorToast("Chức năng đang bảo trì"))}
                    >
                      <item.icon size={16} />
                      {item.label.toUpperCase()}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Vùng lọc nâng cao */}
      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-6 pt-6 border-t border-gray-100">
              <SelectBox label="Môn học" value={filters.category_id} options={subjectOptions} onChange={(v) => updateFilter("category_id", v)} />
              <SelectBox label="Trình độ" value={filters.level} options={levelOptions} onChange={(v) => updateFilter("level", v)} />
              <SelectBox label="Địa điểm" value={filters.classify} options={locationOptions} onChange={(v) => updateFilter("classify", v)} />
              <SelectBox label="Trạng thái" value={filters.is_active} options={statusOptions} onChange={(v) => updateFilter("is_active", v)} />
              <SelectBox label="Phân lớp" value={filters.no_class} options={noClassOptions} onChange={(v) => updateFilter("no_class", v)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StudentFilter;